import agent from 'superagent'
import AuthService from './AuthService'

export type APIError = {
  code: string
  status: number
  debugMessage: string
  message: string
  data: any
  stackTrace: string
}

type QueryParams = {[k: string]: any}

export type APIResponse<V> =
  | {body: V; ok: true}
  | {
      ok: false
      error: APIError
    }

type APIInput = {
  headers?: {[k: string]: string}
  endpoint: string
  method: 'get' | 'put' | 'patch' | 'post' | 'delete'
  body?: any
  query?: {[k: string]: string}
}

class APIRequestor {
  private BASE_URL = process.env.REACT_APP_BASE_API_URL ?? ''

  private pendingRefreshRequest?: Promise<APIResponse<{jwt: string}>>

  private async sendRequest<V = any>(input: APIInput): Promise<APIResponse<V>> {
    let requestor = agent[input.method](`${this.BASE_URL}${input.endpoint}`)
    if (input.query) {
      requestor = requestor.query(input.query)
    }

    if (input.body) {
      requestor = requestor.send(input.body)
    }

    const headers = {
      ...(input.headers ?? {}),
    }

    const jwt = AuthService.getAuthToken()
    if (jwt) {
      headers.Authorization = `Bearer ${jwt}`
    }

    let apiResponse: APIResponse<V>
    requestor = requestor.set(headers)

    try {
      const response = await requestor
      apiResponse = {
        ok: true,
        body: response.body,
      }
    } catch (error) {
      if (error.response?.body) {
        apiResponse = {
          ok: false,
          error: error.response.body,
        }
      } else {
        apiResponse = {
          ok: false,
          error: {
            code: 'UNK',
            status: error.response?.statusCode ?? (500 as number),
            message: `${error}`,
            debugMessage: `${error}`,
            stackTrace: '',
            data: {},
          },
        }
      }
    }

    if (!apiResponse.ok && apiResponse.error.code === 'token-expired') {
      if (this.pendingRefreshRequest == null) {
        this.pendingRefreshRequest = this.sendRequest({
          endpoint: '/auth/refresh',
          method: 'post',
        })
      }
      const refreshResponse = await this.pendingRefreshRequest
      if (refreshResponse.ok) {
        AuthService.storeAuthToken(refreshResponse.body.jwt)
        apiResponse = await this.sendRequest<V>(input)
      }
    }

    return apiResponse
  }

  public post = async <V>(endpoint: string, data?: any) => {
    return await this.sendRequest<V>({
      method: 'post',
      endpoint,
      body: data,
    })
  }

  public get = async <V>(endpoint: string, params?: QueryParams) => {
    return await this.sendRequest<V>({
      method: 'get',
      endpoint,
      query: params,
    })
  }

  public delete = async <V>(endpoint: string, params?: QueryParams) => {
    return await this.sendRequest<V>({
      method: 'delete',
      endpoint,
      query: params,
    })
  }

  public patch = async <V>(endpoint: string, data?: any) => {
    return await this.sendRequest<V>({
      method: 'patch',
      endpoint,
      body: data,
    })
  }

  public put = async <V>(endpoint: string, data?: any) => {
    return await this.sendRequest<V>({
      method: 'patch',
      endpoint,
      body: data,
    })
  }
}

const api = new APIRequestor()
export default api
